import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Calendar, Upload, FileText, DollarSign, 
  LogOut, CheckCircle, Clock, AlertCircle, Package, Send
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

const VendorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [deliverables, setDeliverables] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // For demo, we'll show mock deliverables since vendor assignments need to be set up
      setDeliverables([
        {
          id: 'del-1',
          project_name: 'Tech Corp Annual Gala',
          deliverable: 'Catering Services',
          deadline: '2025-01-15',
          status: 'in_progress',
          description: 'Full catering for 500 guests including cocktails, dinner, and dessert'
        },
        {
          id: 'del-2',
          project_name: 'Product Launch Event',
          deliverable: 'Sound & Lighting',
          deadline: '2025-01-20',
          status: 'pending',
          description: 'Complete AV setup for product presentation and entertainment'
        }
      ]);

      // Try to get actual invoices
      try {
        const invoicesRes = await api.getInvoices();
        setInvoices(invoicesRes.data.filter(inv => inv.vendor_id === user?.user_id));
      } catch {
        setInvoices([]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400';
      case 'in_progress': return 'bg-blue-500/20 text-blue-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'overdue': return 'bg-red-500/20 text-red-400';
      default: return 'bg-white/10 text-white/60';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle size={16} />;
      case 'in_progress': return <Clock size={16} />;
      case 'pending': return <AlertCircle size={16} />;
      default: return <Package size={16} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="vendor-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0A0A0F] border-r border-white/5 p-6 z-50">
        <Link to="/" className="flex items-center space-x-2 mb-10">
          <div className="text-2xl font-bold tracking-tight">
            <span className="text-[#D4AF37]">NEHPETS</span>
            <span className="text-white">+</span>
          </div>
        </Link>

        <div className="space-y-2 mb-10">
          <Link
            to="/dashboard/vendor"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <LayoutDashboard size={20} />
            <span>Overview</span>
          </Link>
          <Link
            to="/dashboard/vendor/deliverables"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <Calendar size={20} />
            <span>Deliverables</span>
          </Link>
          <Link
            to="/dashboard/vendor/upload"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <Upload size={20} />
            <span>Upload Proof</span>
          </Link>
          <Link
            to="/dashboard/vendor/invoices"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <DollarSign size={20} />
            <span>My Invoices</span>
          </Link>
        </div>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            onClick={handleLogout}
            className="w-full bg-transparent border border-white/20 text-white hover:bg-white hover:text-black text-xs py-3"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-lg p-4 mb-8 flex items-center justify-between">
          <div className="flex items-center">
            <Package className="text-[#D4AF37] mr-3" size={24} />
            <span className="text-white/80">Welcome back, {user?.full_name || 'Vendor'}!</span>
          </div>
          <span className="text-white/40 text-sm">Vendor Portal</span>
        </div>

        <Routes>
          <Route
            path="/"
            element={
              <div>
                <div className="mb-8">
                  <h1 className="text-4xl font-medium mb-2">Vendor Dashboard</h1>
                  <p className="text-white/60">View assignments, upload delivery proofs, and manage invoices</p>
                </div>

                {loading ? (
                  <div className="text-white/60">Loading...</div>
                ) : (
                  <>
                    {/* Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="flex items-center mb-4">
                          <Package className="text-[#D4AF37]" size={24} />
                        </div>
                        <div className="text-3xl font-bold text-[#D4AF37] mb-2">
                          {deliverables.length}
                        </div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Assigned Tasks</div>
                      </div>
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="flex items-center mb-4">
                          <Clock className="text-blue-400" size={24} />
                        </div>
                        <div className="text-3xl font-bold text-blue-400 mb-2">
                          {deliverables.filter(d => d.status === 'in_progress').length}
                        </div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">In Progress</div>
                      </div>
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="flex items-center mb-4">
                          <CheckCircle className="text-green-400" size={24} />
                        </div>
                        <div className="text-3xl font-bold text-green-400 mb-2">
                          {deliverables.filter(d => d.status === 'completed').length}
                        </div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Completed</div>
                      </div>
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="flex items-center mb-4">
                          <DollarSign className="text-yellow-400" size={24} />
                        </div>
                        <div className="text-3xl font-bold text-yellow-400 mb-2">
                          {invoices.length}
                        </div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Invoices</div>
                      </div>
                    </div>

                    {/* Assigned Deliverables */}
                    <div className="mb-8">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-medium">My Deliverables</h2>
                        <Link to="/dashboard/vendor/deliverables">
                          <Button variant="ghost" size="sm" className="text-[#D4AF37]">
                            View All
                          </Button>
                        </Link>
                      </div>
                      
                      {deliverables.length === 0 ? (
                        <div className="bg-[#0A0A0F] border border-white/5 p-12 text-center rounded">
                          <Calendar className="mx-auto mb-4 text-white/20" size={64} />
                          <h3 className="text-xl font-medium mb-2">No Assignments Yet</h3>
                          <p className="text-white/60">Your assigned tasks will appear here once you are added to projects.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {deliverables.map((del) => (
                            <div
                              key={del.id}
                              className="bg-[#0A0A0F] border border-white/5 p-4 rounded hover:border-[#D4AF37]/30 transition-all"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="font-medium">{del.deliverable}</h4>
                                  <p className="text-sm text-white/60">{del.project_name}</p>
                                  <p className="text-xs text-white/40 mt-1">{del.description}</p>
                                </div>
                                <div className="text-right">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs ${getStatusColor(del.status)}`}>
                                    {getStatusIcon(del.status)}
                                    <span className="ml-1">{del.status?.replace('_', ' ').toUpperCase()}</span>
                                  </span>
                                  <p className="text-sm text-white/60 mt-2">Due: {del.deadline}</p>
                                </div>
                              </div>
                              <div className="flex space-x-2 mt-4">
                                <Button size="sm" variant="outline" className="border-white/20">
                                  <Upload size={14} className="mr-1" /> Upload Proof
                                </Button>
                                {del.status !== 'completed' && (
                                  <Button size="sm" className="bg-[#D4AF37] hover:bg-[#B8963A] text-black">
                                    Mark Complete
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Submit Invoice */}
                    <div className="bg-gradient-to-r from-[#D4AF37]/5 to-transparent border border-[#D4AF37]/20 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-medium mb-1">Submit Invoice</h3>
                          <p className="text-white/60 text-sm">
                            Submit an invoice for completed deliverables
                          </p>
                        </div>
                        <Link to="/dashboard/vendor/invoices">
                          <Button className="bg-[#D4AF37] hover:bg-[#B8963A] text-black">
                            <FileText size={16} className="mr-2" />
                            Submit Invoice
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </>
                )}
              </div>
            }
          />
          
          <Route path="/deliverables" element={<div className="text-white">Full Deliverables View - Coming Soon</div>} />
          <Route path="/upload" element={<UploadProof deliverables={deliverables} />} />
          <Route path="/invoices" element={<VendorInvoices invoices={invoices} />} />
        </Routes>
      </main>
    </div>
  );
};

// Upload Proof Component
const UploadProof = ({ deliverables }) => {
  const [form, setForm] = useState({
    deliverableId: '',
    description: '',
    files: []
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.deliverableId) {
      toast.error('Please select a deliverable');
      return;
    }
    
    setLoading(true);
    try {
      // Simulate upload
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Proof of delivery uploaded successfully');
      setForm({ deliverableId: '', description: '', files: [] });
    } catch (error) {
      toast.error('Failed to upload proof');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-medium mb-6">Upload Delivery Proof</h1>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="text-white/80 text-sm block mb-2">Select Deliverable *</label>
          <select
            value={form.deliverableId}
            onChange={(e) => setForm({ ...form, deliverableId: e.target.value })}
            className="w-full bg-[#0A0A0F] border border-white/10 rounded px-4 py-3 text-white"
            required
          >
            <option value="">Select a deliverable...</option>
            {deliverables.map((d) => (
              <option key={d.id} value={d.id}>
                {d.project_name} - {d.deliverable}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-white/80 text-sm block mb-2">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Add notes about the delivery..."
            rows={4}
            className="w-full bg-[#0A0A0F] border border-white/10 rounded px-4 py-3 text-white"
          />
        </div>

        <div>
          <label className="text-white/80 text-sm block mb-2">Upload Files</label>
          <div className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center hover:border-[#D4AF37]/50 transition-all cursor-pointer">
            <Upload className="mx-auto mb-4 text-white/40" size={48} />
            <p className="text-white/60 mb-2">Drag and drop files here</p>
            <p className="text-white/40 text-sm">Photos, videos, or documents (max 100MB)</p>
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black py-4"
        >
          {loading ? 'Uploading...' : 'Submit Proof'}
          <Send size={16} className="ml-2" />
        </Button>
      </form>
    </div>
  );
};

// Vendor Invoices Component
const VendorInvoices = ({ invoices }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    deliverableId: '',
    amount: '',
    description: '',
    bankName: '',
    accountNumber: '',
    accountName: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    toast.success('Invoice submitted for review');
    setShowForm(false);
    setForm({
      deliverableId: '',
      amount: '',
      description: '',
      bankName: '',
      accountNumber: '',
      accountName: ''
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-medium">My Invoices</h1>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
        >
          <FileText size={16} className="mr-2" />
          {showForm ? 'Cancel' : 'New Invoice'}
        </Button>
      </div>

      {showForm && (
        <div className="bg-[#0A0A0F] border border-white/10 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium mb-4">Submit New Invoice</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/80 text-sm block mb-2">Amount (₦) *</label>
                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  required
                />
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Bank Name *</label>
                <input
                  type="text"
                  value={form.bankName}
                  onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/80 text-sm block mb-2">Account Number *</label>
                <input
                  type="text"
                  value={form.accountNumber}
                  onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  required
                />
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Account Name *</label>
                <input
                  type="text"
                  value={form.accountName}
                  onChange={(e) => setForm({ ...form, accountName: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-white/80 text-sm block mb-2">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
              />
            </div>
            <Button type="submit" className="bg-[#D4AF37] hover:bg-[#B8963A] text-black">
              Submit Invoice
            </Button>
          </form>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="bg-[#0A0A0F] border border-white/5 p-12 text-center rounded">
          <DollarSign className="mx-auto mb-4 text-white/20" size={64} />
          <h3 className="text-xl font-medium mb-2">No Invoices</h3>
          <p className="text-white/60">Your submitted invoices will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {invoices.map((inv) => (
            <div key={inv.invoice_id} className="bg-[#0A0A0F] border border-white/5 p-4 rounded">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{inv.invoice_number}</h4>
                  <p className="text-sm text-white/60">{inv.description}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-[#D4AF37]">₦{inv.amount?.toLocaleString()}</p>
                  <p className="text-xs text-white/40">{inv.status}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VendorDashboard;
